import dayjs from 'dayjs';

// Range validation for the realtime report export.
//
// Every preset range fills all four inputs (see Export.tsx applyRange); `custom`
// fills none, and it is the default selection. Left partly empty — either
// because the user picked `custom` and never filled it in, or because they
// picked a preset and then cleared a field with the DatePicker's clear button —
// the export handlers call .format() on a null dayjs and the download fails
// silently. Symptom reported in issue #201.

// Reading order. The first gap is the one the UI opens, so the user is walked
// left-to-right along the row rather than dropped at an arbitrary field.
export const CUSTOM_RANGE_FIELD_ORDER = ['startDate', 'startTime', 'endDate', 'endTime'];

/**
 * The first range field still empty, or null when nothing is missing.
 *
 * Applied to any range — custom or preset — because a preset's four values are
 * kept in the same state as a custom range's, and can be cleared out from
 * under the preset after it was picked. Blocking only the `custom` case would
 * let a cleared-preset submit through to a null `.format()` crash.
 */
export const firstMissingRangeField = (values: any) =>
    CUSTOM_RANGE_FIELD_ORDER.find((field: string) => !values?.[field]) ?? null;

/**
 * Combine a date picker's value with a time picker's into a single instant.
 *
 * The two are separate inputs, so neither alone says when the window starts.
 * Falls back to midnight when the time is absent, matching how the range-length
 * check has always treated a missing time.
 */
export const composeInstant = (date: any, time: any) => {
    if (!date) return null;
    const composed = dayjs(`${date.format('YYYY-MM-DD')} ${time ? time.format('HH:mm') : '00:00'}`);
    return composed.isValid() ? composed : null;
};

/**
 * Whether the window runs forwards.
 *
 * Checked separately from the range-length limit, which only ever asked whether
 * the window was too *long*: a backwards range has a negative length, so it
 * passed that test trivially. Equal endpoints are rejected too — a zero-length
 * window exports nothing.
 *
 * Date and time are separate inputs, so a picker `minDate` cannot express this:
 * same-day with an end time before the start time is still backwards.
 */
export const isRangeChronological = (values: any) => {
    const start = composeInstant(values?.startDate, values?.startTime);
    const end = composeInstant(values?.endDate, values?.endTime);
    // An incomplete range is not a *disordered* one — completeness is checked by
    // firstMissingRangeField, and reporting both at once would be noise.
    if (!start || !end) return true;
    return end.isAfter(start);
};
